import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import PricingSection from "@/components/PricingSection";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  FileText, Settings, LogOut, TrendingUp, Save, Send, Eye, ArrowLeft,
  Plus, Trash2, Type, DollarSign, Image, PenTool, Calendar as CalendarIcon,
  GripVertical, Target, Sparkles, Plug, Paperclip, Download, File, FileSpreadsheet,
  Upload, X, Loader2, FileDown, Lock, CheckCircle2, XCircle, AlertTriangle, Bell, Mail,
  User, Phone, Globe, Shield, FolderOpen, BarChart3, Menu
} from "lucide-react";
import DocuSignAgreementCard from "@/pages/DocuSignAgreementCard";
import DocuSignTracker from "@/pages/DocuSignTracker";
import PaymentTracker from "@/pages/PaymentTracker";

// Generate simple UUID
const generateId = () => Math.random().toString(36).substring(2, 15);

// Check if proposal is in a read-only state
const isReadOnlyStatus = (status) => ['accepted', 'declined'].includes(status);

// Status banner configuration
const STATUS_CONFIG = {
  accepted: {
    icon: CheckCircle2,
    label: "Accepted",
    color: "bg-green-50 border-green-200 text-green-800",
    iconColor: "text-green-600",
    message: "This proposal has been accepted. The content is now locked."
  },
  declined: {
    icon: XCircle,
    label: "Declined",
    color: "bg-red-50 border-red-200 text-red-800",
    iconColor: "text-red-600",
    message: "This proposal has been declined. You can only view and download the content."
  },
  sent: {
    icon: Send,
    label: "Sent",
    color: "bg-blue-50 border-blue-200 text-blue-800",
    iconColor: "text-blue-600",
    message: "This proposal has been sent to the prospect and is awaiting response."
  }
};

// Status Banner Component
const StatusBanner = ({ status, acceptedAt, declinedAt }) => {
  const config = STATUS_CONFIG[status];
  if (!config) return null;
  
  const Icon = config.icon;
  const timestamp = status === 'accepted' ? acceptedAt : declinedAt;
  
  return (
    <div className={`mb-6 p-4 rounded-lg border ${config.color} flex items-start gap-3`} data-testid="status-banner">
      <Icon className={`w-5 h-5 mt-0.5 ${config.iconColor}`} />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{config.label}</span>
          {timestamp && (
            <span className="text-sm opacity-75">
              on {new Date(timestamp).toLocaleDateString()}
            </span>
          )}
        </div>
        <p className="text-sm mt-1 opacity-90">{config.message}</p>
      </div>
      <Lock className="w-4 h-4 opacity-50" />
    </div>
  );
};

// File type icons
const getFileIcon = (fileType) => {
  if (fileType === '.pdf') return <FileText className="w-5 h-5 text-red-500" />;
  if (fileType === '.doc' || fileType === '.docx') return <File className="w-5 h-5 text-blue-500" />;
  if (fileType === '.xls' || fileType === '.xlsx') return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
  return <FileText className="w-5 h-5 text-muted-foreground" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Template icons mapping
const TemplateIcon = ({ icon }) => {
  const icons = {
    TrendingUp: TrendingUp,
    Calendar: CalendarIcon,
    Target: Target
  };
  const Icon = icons[icon] || FileText;
  return <Icon className="w-6 h-6" />;
};

// AUM Pricing Section Component
const AUMPricingSection = ({ section, updateSection, headers, readOnly = false }) => {
  const [calculating, setCalculating] = useState(false);
  const [feeResult, setFeeResult] = useState(null);
  const [hasFeeSchedule, setHasFeeSchedule] = useState(true);
  const [savedFeeSchedule, setSavedFeeSchedule] = useState(null);
  
  // Initialize from saved section content (for proposal reload) or defaults
  const [useOverride, setUseOverride] = useState(section.content.use_override || false);
  const [overrideFeeType, setOverrideFeeType] = useState(section.content.override_fee_type || "flat");
  const [overrideRate, setOverrideRate] = useState(section.content.override_rate || 1.0);
  const [overrideTiers, setOverrideTiers] = useState(
    section.content.override_tiers || [
      { min_amount: 0, max_amount: 500000, rate: 1.0 },
      { min_amount: 500001, max_amount: null, rate: 0.85 }
    ]
  );

  // Load saved fee schedule on mount (for defaults when not using override)
  useEffect(() => {
    const loadFeeSchedule = async () => {
      try {
        const response = await axios.get(`${API}/settings/fee-schedule`, { headers, withCredentials: true });
        if (response.data) {
          setSavedFeeSchedule(response.data);
          // Only pre-populate override values if NOT already using a saved override
          if (!section.content.use_override) {
            setOverrideFeeType(response.data.fee_type || "flat");
            if (response.data.flat_rate) {
              setOverrideRate(response.data.flat_rate);
            }
            if (response.data.tiers && response.data.tiers.length > 0) {
              setOverrideTiers(response.data.tiers);
            }
          }
        }
      } catch (error) {
        // No fee schedule set yet
      }
    };
    loadFeeSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save override settings to section content whenever they change (but not on initial load)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Only save the override-related fields, preserve other content
    updateSection(section.id, {
      use_override: useOverride,
      override_fee_type: overrideFeeType,
      override_rate: overrideRate,
      override_tiers: overrideTiers
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useOverride, overrideFeeType, overrideRate, JSON.stringify(overrideTiers)]);

  // When fee type changes, populate from saved schedule if it matches
  const handleFeeTypeChange = (newType) => {
    setOverrideFeeType(newType);
    if (savedFeeSchedule && savedFeeSchedule.fee_type === newType) {
      // Populate from saved schedule
      if (newType === "flat" && savedFeeSchedule.flat_rate) {
        setOverrideRate(savedFeeSchedule.flat_rate);
      } else if (savedFeeSchedule.tiers && savedFeeSchedule.tiers.length > 0) {
        setOverrideTiers([...savedFeeSchedule.tiers]);
      }
    }
  };

  const calculateFee = async (aum) => {
    if (!aum || aum <= 0) {
      setFeeResult(null);
      updateSection(section.id, { aum: 0, calculated_fee: 0, fee_display: "" });
      return;
    }

    setCalculating(true);
    try {
      const requestData = { aum };
      
      // If using override, send the custom fee schedule
      if (useOverride) {
        // Use saved display preference or default
        const displayPref = savedFeeSchedule?.display_preference || "annual_with_rate";
        
        if (overrideFeeType === "flat") {
          requestData.fee_schedule = {
            fee_type: "flat",
            flat_rate: overrideRate,
            display_preference: displayPref
          };
        } else {
          requestData.fee_schedule = {
            fee_type: overrideFeeType,
            tiers: overrideTiers,
            display_preference: displayPref
          };
        }
      }

      const response = await axios.post(
        `${API}/calculate-fee`,
        requestData,
        { headers, withCredentials: true }
      );
      
      setFeeResult(response.data);
      setHasFeeSchedule(true);
      
      // Build the content update with all fee data including override settings
      const contentUpdate = {
        aum,
        calculated_fee: response.data.annual_fee,
        fee_display: response.data.display_text,
        effective_rate: response.data.effective_rate,
        breakdown: response.data.breakdown,
        display_preference: response.data.display_preference,
        // Always save override settings so they persist
        use_override: useOverride,
        override_fee_type: overrideFeeType,
        override_rate: overrideRate,
        override_tiers: overrideTiers
      };
      
      updateSection(section.id, contentUpdate);
    } catch (error) {
      if (error.response?.status === 400) {
        setHasFeeSchedule(false);
        toast.error("Please set up your fee schedule in Settings first");
      } else {
        toast.error("Failed to calculate fee");
      }
    } finally {
      setCalculating(false);
    }
  };

  // Debounced calculation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (section.content.aum > 0) {
        calculateFee(section.content.aum);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.content.aum, useOverride, overrideFeeType, overrideRate, overrideTiers]);

  // Format number with commas
  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || value === "" || value === 0) return "";
    return Number(value).toLocaleString();
  };

  // Parse formatted number back to number
  const parseFormattedNumber = (value) => {
    if (!value) return 0;
    return parseFloat(String(value).replace(/,/g, "")) || 0;
  };

  return (
    <div className="space-y-6">
      {/* AUM Input */}
      <div>
        <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">
          Assets Under Management (AUM)
        </Label>
        <div className="relative mt-2">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">$</span>
          <Input
            type="text"
            value={formatNumberWithCommas(section.content.aum)}
            onChange={(e) => {
              const aum = parseFormattedNumber(e.target.value);
              updateSection(section.id, { aum });
            }}
            placeholder="1,000,000"
            className="pl-8 font-mono text-lg"
            data-testid="aum-input"
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Override Option - Hide in read-only mode */}
      {!readOnly && (
        <div className="p-4 bg-sand rounded-lg space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="override-rate"
              checked={useOverride}
              onChange={(e) => setUseOverride(e.target.checked)}
              className="rounded border-stone"
            />
            <Label htmlFor="override-rate" className="text-sm cursor-pointer">
              Use different fee for this proposal
            </Label>
          </div>
          
          {useOverride && (
            <div className="space-y-4 pt-2 border-t border-stone/30">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Fee Type</Label>
                <select
                  value={overrideFeeType}
                  onChange={(e) => handleFeeTypeChange(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="flat">Flat Rate</option>
                  <option value="graduated">Graduated (Blended)</option>
                  <option value="cliff">Cliff</option>
                </select>
                {savedFeeSchedule && savedFeeSchedule.fee_type === overrideFeeType && (
                  <p className="text-xs text-muted-foreground mt-1 italic">
                    Pre-filled from your saved fee schedule
                  </p>
                )}
              </div>
              
              {overrideFeeType === "flat" ? (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Rate</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={overrideRate}
                      onChange={(e) => setOverrideRate(parseFloat(e.target.value) || 0)}
                      className="w-24 font-mono"
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                </div>
              ) : (
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Tiers</Label>
                <div className="space-y-2">
                  {overrideTiers.map((tier, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground w-8">$</span>
                      <Input
                        type="text"
                        value={formatNumberWithCommas(tier.min_amount)}
                        onChange={(e) => {
                          const newTiers = [...overrideTiers];
                          newTiers[idx].min_amount = parseFormattedNumber(e.target.value);
                          setOverrideTiers(newTiers);
                        }}
                        className="w-28 font-mono text-sm"
                        disabled={idx === 0}
                      />
                      <span className="text-muted-foreground">to</span>
                      {tier.max_amount === null ? (
                        <span className="w-28 text-center text-muted-foreground">∞</span>
                      ) : (
                        <Input
                          type="text"
                          value={formatNumberWithCommas(tier.max_amount)}
                          onChange={(e) => {
                            const newTiers = [...overrideTiers];
                            newTiers[idx].max_amount = parseFormattedNumber(e.target.value);
                            if (idx < newTiers.length - 1) {
                              newTiers[idx + 1].min_amount = parseFormattedNumber(e.target.value) + 1;
                            }
                            setOverrideTiers(newTiers);
                          }}
                          className="w-28 font-mono text-sm"
                        />
                      )}
                      <span className="text-muted-foreground">@</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={tier.rate}
                        onChange={(e) => {
                          const newTiers = [...overrideTiers];
                          newTiers[idx].rate = parseFloat(e.target.value) || 0;
                          setOverrideTiers(newTiers);
                        }}
                        className="w-16 font-mono text-sm"
                      />
                      <span className="text-muted-foreground">%</span>
                      {overrideTiers.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            const newTiers = overrideTiers.filter((_, i) => i !== idx);
                            if (newTiers.length > 0) newTiers[newTiers.length - 1].max_amount = null;
                            setOverrideTiers(newTiers);
                          }}
                        >
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const lastTier = overrideTiers[overrideTiers.length - 1];
                      const newMax = (lastTier.min_amount || 0) + 500000;
                      setOverrideTiers([
                        ...overrideTiers.slice(0, -1),
                        { ...lastTier, max_amount: newMax },
                        { min_amount: newMax + 1, max_amount: null, rate: Math.max(0.25, lastTier.rate - 0.15) }
                      ]);
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Tier
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Show fee summary in read-only mode if override was used */}
      {readOnly && useOverride && (
        <div className="p-4 bg-sand rounded-lg">
          <p className="text-sm text-muted-foreground">
            Custom fee rate applied: {overrideFeeType === "flat" ? `${overrideRate}% flat` : `${overrideFeeType} tiered`}
          </p>
        </div>
      )}

      {/* Fee Calculation Result */}
      {calculating ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Calculating...
        </div>
      ) : feeResult ? (
        <div className="p-4 bg-forest/5 border border-forest/20 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-forest">Annual Advisory Fee</span>
            <span className="font-mono text-xl font-bold text-forest">
              ${feeResult.annual_fee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            {feeResult.display_text}
          </p>
          {/* Only show breakdown when display_preference is full_breakdown */}
          {feeResult.display_preference === "full_breakdown" && feeResult.breakdown && feeResult.breakdown.length > 1 && (
            <div className="mt-3 pt-3 border-t border-forest/10">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Breakdown</p>
              {feeResult.breakdown.map((tier, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{tier.range} @ {tier.rate}%</span>
                  <span className="font-mono">${tier.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : !hasFeeSchedule ? (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            No fee schedule configured. <Link to="/settings" className="underline font-medium">Set up your fee schedule</Link> in Settings, or use the override option above.
          </p>
        </div>
      ) : null}

      {/* Services Included - What's covered by AUM fee */}
      <div>
        <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground mb-3 block">
          Services Included
        </Label>
        <p className="text-xs text-muted-foreground mb-3">What's included in your AUM fee</p>
        <div className="space-y-3">
          {(section.content.additional_services || []).map((service, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-sand/50 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-forest flex-shrink-0" />
              <Input
                value={service.description}
                onChange={(e) => {
                  const newServices = [...(section.content.additional_services || [])];
                  newServices[idx] = { ...newServices[idx], description: e.target.value };
                  updateSection(section.id, { additional_services: newServices });
                }}
                placeholder="Service description (e.g., Quarterly portfolio reviews)"
                className="flex-1"
                disabled={readOnly}
              />
              <div className="flex items-center gap-2">
                <select
                  value={service.included ? "included" : "value"}
                  onChange={(e) => {
                    const newServices = [...(section.content.additional_services || [])];
                    if (e.target.value === "included") {
                      newServices[idx] = { ...newServices[idx], amount: 0, included: true };
                    } else {
                      newServices[idx] = { ...newServices[idx], included: false };
                    }
                    updateSection(section.id, { additional_services: newServices });
                  }}
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                  disabled={readOnly}
                >
                  <option value="included">Included</option>
                  <option value="value">Show Value</option>
                </select>
                {!service.included && (
                  <div className="relative w-28">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="text"
                      value={service.amount ? service.amount.toLocaleString() : ""}
                      onChange={(e) => {
                        const newServices = [...(section.content.additional_services || [])];
                        const value = parseFloat(e.target.value.replace(/,/g, "")) || 0;
                        newServices[idx] = { ...newServices[idx], amount: value };
                        updateSection(section.id, { additional_services: newServices });
                      }}
                      className="pl-7 font-mono"
                      placeholder="Value"
                      disabled={readOnly}
                    />
                  </div>
                )}
              </div>
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    const newServices = (section.content.additional_services || []).filter((_, i) => i !== idx);
                    updateSection(section.id, { additional_services: newServices });
                  }}
                  className="h-8 w-8 text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newServices = [...(section.content.additional_services || []), { description: "", amount: 0, included: true }];
                updateSection(section.id, { additional_services: newServices });
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProposalBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout, token, invalidateCache, mobileMenuOpen, setMobileMenuOpen } = useAuth();
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(!id);
  const [templates, setTemplates] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [proposal, setProposal] = useState({
    title: "",
    prospect_name: "",
    prospect_email: "",
    description: "",
    sections: [],
    valid_until: null,
    total_amount: null,
    docusign_config: null,
    payment_config: null,
    reminder_settings_override: null
  });
  const [globalReminderSettings, setGlobalReminderSettings] = useState(null);
  const [emailConnected, setEmailConnected] = useState(false); // Default to not connected until verified
  const [stripeConnected, setStripeConnected] = useState(null); // null = loading, true/false = status
  const [docusignConnected, setDocusignConnected] = useState(null);
  const [showWelcomeEmailPreview, setShowWelcomeEmailPreview] = useState(false);
  const [welcomeEmailContent, setWelcomeEmailContent] = useState({
    custom_intro: "",
    custom_outro: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (id) {
      loadProposal();
      loadAttachments();
    } else {
      loadTemplates();
    }
    loadGlobalReminderSettings();
    checkIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const checkIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations/status`, { headers, withCredentials: true });
      setStripeConnected(response.data?.stripe?.connected || false);
      setDocusignConnected(response.data?.docusign?.connected || false);
      // Check actual email connection status from API
      const isEmailConnected = response.data?.email?.connected || false;
      // TEST MODE BYPASS: Allow test user to access welcome email modal without real Gmail connection
      const isTestUser = user?.email === 'advisor.template@example.com';
      setEmailConnected(isEmailConnected || isTestUser);
    } catch (error) {
      setStripeConnected(false);
      setDocusignConnected(false);
      // Only bypass for test user on error
      const isTestUser = user?.email === 'advisor.template@example.com';
      setEmailConnected(isTestUser);
    }
  };

  const loadGlobalReminderSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/reminders`, { headers, withCredentials: true });
      setGlobalReminderSettings(response.data);
    } catch (error) {
      // No settings yet
      setGlobalReminderSettings({ reminders_enabled: false });
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`, { headers, withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to load templates");
    }
  };

  const loadAttachments = async () => {
    if (!id) return;
    try {
      const response = await axios.get(`${API}/proposals/${id}/attachments`, { headers, withCredentials: true });
      setAttachments(response.data);
    } catch (error) {
      console.error("Failed to load attachments");
    }
  };

  const loadProposal = async () => {
    try {
      const response = await axios.get(`${API}/proposals/${id}`, { headers, withCredentials: true });
      setProposal({
        ...response.data,
        valid_until: response.data.valid_until ? new Date(response.data.valid_until) : null
      });
    } catch (error) {
      toast.error("Failed to load proposal");
      navigate("/proposals");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error(`File type not allowed. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB");
      return;
    }
    
    setUploading(true);
    try {
      let proposalId = id;
      
      // If no proposal ID yet, save as draft first
      if (!proposalId) {
        const data = {
          ...proposal,
          valid_until: proposal.valid_until?.toISOString(),
          total_amount: proposal.total_amount ? parseFloat(proposal.total_amount) : null
        };
        
        const createResponse = await axios.post(`${API}/proposals`, data, { headers, withCredentials: true });
        proposalId = createResponse.data.proposal_id;
        toast.success("Proposal saved as draft");
        
        // Navigate to the new proposal URL (this will update `id`)
        navigate(`/proposals/${proposalId}`, { replace: true });
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/proposals/${proposalId}/attachments`,
        formData,
        { 
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          withCredentials: true 
        }
      );
      
      setAttachments(prev => [...prev, response.data]);
      toast.success(`${file.name} uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteAttachment = async (attachmentId, filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;
    
    try {
      await axios.delete(`${API}/proposals/${id}/attachments/${attachmentId}`, { headers, withCredentials: true });
      setAttachments(attachments.filter(a => a.attachment_id !== attachmentId));
      toast.success("Attachment deleted");
    } catch (error) {
      toast.error("Failed to delete attachment");
    }
  };

  const downloadAttachment = async (attachmentId, filename) => {
    try {
      const response = await axios.get(
        `${API}/proposals/${id}/attachments/${attachmentId}`,
        { headers, withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download attachment");
    }
  };

  const applyTemplate = (template) => {
    // Deep clone sections and generate new IDs
    // Auto-populate advisor_info sections with current user data
    const sections = template.sections.map(section => {
      const newSection = {
        ...section,
        id: generateId(),
        content: { ...section.content }
      };
      
      // If this is an advisor_info section, populate with user data
      if (section.type === 'advisor_info' && user) {
        newSection.content = {
          ...newSection.content,
          name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '',
          title: user.title || '',
          designations: user.designations || '',
          company_name: user.company_name || '',
          phone: user.phone || '',
          email: user.contact_email || user.email || '',
          company_website: user.company_website || '',
          profile_picture_url: user.profile_picture_url || '',
          broker_check_link: user.broker_check_link || ''
        };
      }
      
      return newSection;
    });
    
    setProposal(prevProposal => ({
      ...prevProposal,
      title: template.name,
      description: template.description,
      sections: sections
    }));
    
    setShowTemplateDialog(false);
    toast.success(`Template "${template.name}" applied`);
  };

  const startFromScratch = () => {
    setShowTemplateDialog(false);
  };

  const saveProposal = async () => {
    // Allow saving anytime - no required fields for drafts
    setSaving(true);
    try {
      // Only send fields that the update endpoint accepts
      // Send null instead of empty strings for optional email fields
      const data = {
        title: proposal.title || null,
        prospect_name: proposal.prospect_name || null,
        prospect_email: proposal.prospect_email || null,
        description: proposal.description || null,
        sections: proposal.sections,
        valid_until: proposal.valid_until?.toISOString() || null,
        total_amount: proposal.total_amount ? parseFloat(proposal.total_amount) : null,
        docusign_config: proposal.docusign_config,
        payment_config: proposal.payment_config,
        followup_settings: proposal.followup_settings
      };

      if (id) {
        await axios.put(`${API}/proposals/${id}`, data, { headers, withCredentials: true });
        toast.success("Proposal saved");
      } else {
        const response = await axios.post(`${API}/proposals`, data, { headers, withCredentials: true });
        toast.success("Proposal created");
        navigate(`/proposals/${response.data.proposal_id}`);
      }
      // Invalidate cache so lists show updated data
      invalidateCache('proposals');
      invalidateCache('stats');
    } catch (error) {
      console.error('Save error:', error.response?.data || error.message);
      toast.error("Failed to save proposal");
    } finally {
      setSaving(false);
    }
  };

  const sendProposal = async () => {
    if (!id) {
      toast.error("Save proposal first");
      return;
    }

    // Validate required fields before sending
    const missingFields = [];
    if (!proposal.prospect_name) missingFields.push("Prospect Name");
    if (!proposal.prospect_email) missingFields.push("Prospect Email");
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(", ")}`);
      return;
    }

    setSending(true);
    try {
      // Auto-save before sending to ensure latest changes are captured
      const data = {
        title: proposal.title || null,
        prospect_name: proposal.prospect_name || null,
        prospect_email: proposal.prospect_email || null,
        description: proposal.description || null,
        sections: proposal.sections,
        valid_until: proposal.valid_until?.toISOString() || null,
        total_amount: proposal.total_amount ? parseFloat(proposal.total_amount) : null,
        docusign_config: proposal.docusign_config,
        payment_config: proposal.payment_config
      };
      await axios.put(`${API}/proposals/${id}`, data, { headers, withCredentials: true });
      
      // Now send
      await axios.post(`${API}/proposals/${id}/send`, {}, { headers, withCredentials: true });
      toast.success("Proposal sent to prospect!");
      loadProposal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send proposal");
    } finally {
      setSending(false);
    }
  };

  const downloadPDF = async () => {
    if (!id) {
      toast.error("Save proposal first");
      return;
    }

    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API}/proposals/${id}/pdf`,
        { 
          headers, 
          withCredentials: true, 
          responseType: 'blob' 
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${proposal.title || 'proposal'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const addSection = (type) => {
    const newSection = {
      id: generateId(),
      type,
      order: 0, // Will be set correctly below using functional update
      content: getSectionDefaults(type)
    };
    // Use functional update pattern to ensure we have the latest state
    setProposal(prevProposal => {
      const updatedSections = [...prevProposal.sections, { ...newSection, order: prevProposal.sections.length }];
      return { ...prevProposal, sections: updatedSections };
    });
  };

  const getSectionDefaults = (type) => {
    switch (type) {
      case "text":
        return { title: "", body: "" };
      case "pricing":
        return { 
          items: [{ description: "", amount: 0 }],
          total: 0
        };
      case "aum_pricing":
        return {
          aum: 0,
          calculated_fee: 0,
          fee_display: "",
          additional_services: []
        };
      case "image":
        return { url: "", caption: "" };
      case "signature":
        return { label: "Signature", required: true };
      case "advisor_info":
        return {
          name: user?.name || "",
          title: user?.title || "",
          designations: user?.designations || "",
          company_name: user?.company_name || "",
          phone: user?.phone || "",
          email: user?.contact_email || user?.email || "",
          company_website: user?.company_website || "",
          profile_picture_url: user?.profile_picture_url || "",
          broker_check_link: user?.broker_check_link || ""
        };
      case "brokercheck":
        return {
          link: user?.broker_check_link || "",
          description: "Verify my credentials on FINRA BrokerCheck"
        };
      case "onboarding_software":
        return {
          link: user?.planning_software_link || "",
          name: "Client Portal",
          description: "Access your personalized client portal to view documents, track progress, and manage your account."
        };
      default:
        return {};
    }
  };

  const updateSection = (sectionId, content) => {
    setProposal(prevProposal => ({
      ...prevProposal,
      sections: prevProposal.sections.map(s => 
        s.id === sectionId ? { ...s, content: { ...s.content, ...content } } : s
      )
    }));
  };

  const removeSection = (sectionId) => {
    setProposal(prevProposal => ({
      ...prevProposal,
      sections: prevProposal.sections.filter(s => s.id !== sectionId)
    }));
  };

  const calculateTotal = () => {
    let pricingTotal = 0;
    let aumFeeTotal = 0;
    let recurringTotal = 0;
    
    proposal.sections.forEach(s => {
      if (s.type === "pricing") {
        const content = s.content || {};
        const paymentType = content.payment_type || "one_time";
        
        // Calculate based on payment type
        if (paymentType === "one_time" && content.items) {
          content.items.forEach(item => {
            pricingTotal += parseFloat(item.amount) || 0;
          });
        } else if (paymentType === "split" && content.split_payments) {
          content.split_payments.forEach(payment => {
            pricingTotal += parseFloat(payment.amount) || 0;
          });
        } else if (paymentType === "subscription" && content.subscription) {
          recurringTotal += parseFloat(content.subscription.amount) || 0;
        } else if (paymentType === "hybrid") {
          pricingTotal += parseFloat(content.onboarding_fee) || 0;
          if (content.subscription) {
            recurringTotal += parseFloat(content.subscription.amount) || 0;
          }
        }
      }
      if (s.type === "aum_pricing" && s.content.calculated_fee) {
        aumFeeTotal += parseFloat(s.content.calculated_fee) || 0;
      }
    });
    
    return { pricingTotal, aumFeeTotal, recurringTotal, total: pricingTotal + aumFeeTotal };
  };

  useEffect(() => {
    const { total } = calculateTotal();
    if (total > 0 && total !== proposal.total_amount) {
      setProposal(p => ({ ...p, total_amount: total }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.sections]);

  return (
    <div className="min-h-screen bg-sand" data-testid="proposal-builder">
      {/* Template Selection Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Create New Proposal</DialogTitle>
            <DialogDescription>
              Choose a template to get started quickly, or start from scratch.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 mt-4">
            {templates.map((template) => (
              <Card 
                key={template.id}
                className="border-stone hover:border-forest/50 cursor-pointer transition-all hover:shadow-md"
                onClick={() => applyTemplate(template)}
                data-testid={`template-${template.id}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-forest/10 rounded-lg flex items-center justify-center text-forest">
                    <TemplateIcon icon={template.icon} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-semibold text-forest">{template.name}</h3>
                    <p className="text-sm text-muted-foreground">{template.description}</p>
                  </div>
                  <Sparkles className="w-5 h-5 text-terracotta" />
                </CardContent>
              </Card>
            ))}
            
            <Card 
              className="border-dashed border-2 border-stone hover:border-forest/30 cursor-pointer transition-all"
              onClick={startFromScratch}
              data-testid="start-from-scratch"
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-stone/50 rounded-lg flex items-center justify-center text-muted-foreground">
                  <Plus className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-semibold text-forest">Start from Scratch</h3>
                  <p className="text-sm text-muted-foreground">Build your proposal from a blank canvas</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile hamburger button */}
      {!mobileMenuOpen && (
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="md:hidden fixed top-4 left-4 z-40 p-2 bg-forest text-white rounded-lg shadow-lg"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop always visible, Mobile toggled */}
      <aside className={`fixed left-0 top-0 h-screen w-64 bg-forest text-white p-6 flex flex-col z-50 transform transition-transform duration-300 md:translate-x-0 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Mobile close button */}
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="md:hidden absolute top-4 right-4 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
          data-testid="mobile-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <Link to="/" className="flex items-center gap-2 mb-10">
          <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <span className="font-display text-lg font-semibold">ProspectConnect</span>
        </Link>

        <nav className="flex-1 space-y-2">
          <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 text-white/70 hover:text-white transition-colors">
            <TrendingUp className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/proposals" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 text-white">
            <FileText className="w-5 h-5" />
            <span className="font-medium">Proposals</span>
          </Link>
          <Link to="/integrations" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 text-white/70 hover:text-white transition-colors">
            <Plug className="w-5 h-5" />
            <span>Integrations</span>
          </Link>
          <Link to="/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 text-white/70 hover:text-white transition-colors">
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </Link>
        </nav>

        {/* Add Section Buttons - Only show for editable proposals */}
        {!isReadOnlyStatus(proposal.status) && (
          <div className="border-t border-white/10 pt-4 mb-4">
            {/* Quick Add - Info sections */}
            <p className="text-xs uppercase tracking-wider text-white/50 mb-3">Quick Add</p>
            <div className="space-y-1 mb-4">
              <Button 
                variant="ghost" 
                size="sm"
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => addSection("advisor_info")}
                data-testid="add-advisor-info-section"
              >
                <User className="w-4 h-4 mr-2" />
                About Me / Contact
              </Button>
              {user?.broker_check_link && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
                  onClick={() => addSection("brokercheck")}
                  data-testid="add-brokercheck-section"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  BrokerCheck
                </Button>
              )}
              {user?.planning_software_link && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
                  onClick={() => addSection("onboarding_software")}
                  data-testid="add-onboarding-software-section"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Onboarding Software
                </Button>
              )}
            </div>
            
            {/* Main content sections */}
            <p className="text-xs uppercase tracking-wider text-white/50 mb-3">Add Section</p>
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                className="justify-start text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => addSection("text")}
                data-testid="add-text-section"
              >
                <Type className="w-4 h-4 mr-2" />
                Text
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                className="justify-start text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => addSection("pricing")}
                data-testid="add-pricing-section"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Pricing
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                className="justify-start text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => addSection("aum_pricing")}
                data-testid="add-aum-pricing-section"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                AUM Pricing
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                className="justify-start text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => addSection("image")}
                data-testid="add-image-section"
              >
                <Image className="w-4 h-4 mr-2" />
                Image
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                className="justify-start text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => addSection("signature")}
                data-testid="add-signature-section"
              >
                <PenTool className="w-4 h-4 mr-2" />
                Signature
              </Button>
            </div>
          </div>
        )}

        <div className="border-t border-white/10 pt-4">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
            onClick={logout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Status Banner for Accepted/Declined/Sent */}
        {proposal.status && ['accepted', 'declined', 'sent'].includes(proposal.status) && (
          <StatusBanner 
            status={proposal.status} 
            acceptedAt={proposal.accepted_at}
            declinedAt={proposal.declined_at}
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" asChild>
              <Link to="/proposals">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Link>
            </Button>
            <div>
              <h1 className="font-display text-2xl font-bold text-forest">
                {isReadOnlyStatus(proposal.status) ? "View Proposal" : (id ? "Edit Proposal" : "New Proposal")}
              </h1>
              {proposal.status && (
                <Badge variant="outline" className="mt-1 capitalize">
                  {proposal.status}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Template button - only for new proposals */}
            {!id && !isReadOnlyStatus(proposal.status) && (
              <Button 
                variant="outline" 
                onClick={() => setShowTemplateDialog(true)}
                data-testid="change-template-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Templates
              </Button>
            )}
            
            {/* Preview button - always available for saved proposals */}
            {id ? (
              <Button variant="outline" asChild data-testid="preview-btn">
                <Link to={`/view/${id}`} target="_blank">
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Link>
              </Button>
            ) : (
              <Button variant="outline" disabled data-testid="preview-btn-disabled" title="Save proposal first to preview">
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
            )}
            
            {/* Download PDF - available for all saved proposals */}
            {id && (
              <Button
                variant="outline"
                onClick={downloadPDF}
                disabled={downloadingPdf}
                data-testid="download-pdf-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                {downloadingPdf ? "Generating..." : "Download PDF"}
              </Button>
            )}
            
            {/* Save & Send buttons - only for draft/sent (editable) proposals */}
            {!isReadOnlyStatus(proposal.status) && (
              <>
                <Button 
                  variant="outline" 
                  onClick={saveProposal}
                  disabled={saving}
                  data-testid="save-proposal-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Draft"}
                </Button>
                <Button 
                  className="bg-terracotta hover:bg-terracotta/90"
                  onClick={sendProposal}
                  disabled={sending || !id}
                  data-testid="send-proposal-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? "Sending..." : "Send to Prospect"}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card className="border-stone">
              <CardHeader>
                <CardTitle className="font-display text-xl">Proposal Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Proposal Title *</Label>
                  <Input
                    value={proposal.title}
                    onChange={(e) => setProposal({...proposal, title: e.target.value})}
                    placeholder="e.g., Financial Advisory Services"
                    className="mt-2"
                    data-testid="proposal-title-input"
                    disabled={isReadOnlyStatus(proposal.status)}
                  />
                </div>
                <div>
                  <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Description</Label>
                  <Textarea
                    value={proposal.description}
                    onChange={(e) => setProposal({...proposal, description: e.target.value})}
                    placeholder="Brief description of your services..."
                    className="mt-2 min-h-[100px]"
                    data-testid="proposal-description-input"
                    disabled={isReadOnlyStatus(proposal.status)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Sections */}
            {proposal.sections.map((section, index) => (
              <Card 
                key={section.id} 
                className="border-stone"
                draggable={!isReadOnlyStatus(proposal.status)}
                onDragStart={(e) => {
                  if (isReadOnlyStatus(proposal.status)) return;
                  e.dataTransfer.setData('text/plain', index.toString());
                  e.currentTarget.style.opacity = '0.5';
                }}
                onDragEnd={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onDragOver={(e) => {
                  if (isReadOnlyStatus(proposal.status)) return;
                  e.preventDefault();
                  e.currentTarget.style.borderTop = '2px solid #0f392b';
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.borderTop = '';
                }}
                onDrop={(e) => {
                  if (isReadOnlyStatus(proposal.status)) return;
                  e.preventDefault();
                  e.currentTarget.style.borderTop = '';
                  const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                  const toIndex = index;
                  if (fromIndex !== toIndex) {
                    setProposal(prevProposal => {
                      const newSections = [...prevProposal.sections];
                      const [moved] = newSections.splice(fromIndex, 1);
                      newSections.splice(toIndex, 0, moved);
                      newSections.forEach((s, i) => s.order = i);
                      return { ...prevProposal, sections: newSections };
                    });
                  }
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between py-4">
                  <div className="flex items-center gap-3">
                    {!isReadOnlyStatus(proposal.status) && (
                      <GripVertical className="w-5 h-5 text-muted-foreground cursor-grab active:cursor-grabbing" />
                    )}
                    <CardTitle className="font-display text-lg capitalize">
                      {section.type === "advisor_info" ? "About Me / Contact" :
                       section.type === "brokercheck" ? "BrokerCheck" :
                       section.type === "onboarding_software" ? "Onboarding Software" :
                       section.type === "aum_pricing" ? "AUM" :
                       section.type === "pricing" ? "Pricing" :
                       section.type === "text" ? "Text" :
                       section.type === "image" ? "Image" :
                       section.type === "signature" ? "Signature" :
                       section.type.replace('_', ' ')} Section
                    </CardTitle>
                  </div>
                  {!isReadOnlyStatus(proposal.status) && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => removeSection(section.id)}
                      data-testid={`remove-section-${section.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {section.type === "text" && (
                    <div className="space-y-4">
                      <Input
                        value={section.content.title || ""}
                        onChange={(e) => updateSection(section.id, { title: e.target.value })}
                        placeholder="Section Title"
                        disabled={isReadOnlyStatus(proposal.status)}
                      />
                      <Textarea
                        value={section.content.body || ""}
                        onChange={(e) => updateSection(section.id, { body: e.target.value })}
                        placeholder="Section content..."
                        className="min-h-[150px]"
                        disabled={isReadOnlyStatus(proposal.status)}
                      />
                    </div>
                  )}

                  {section.type === "pricing" && (
                    <PricingSection
                      section={section}
                      updateSection={updateSection}
                      readOnly={isReadOnlyStatus(proposal.status)}
                    />
                  )}

                  {section.type === "aum_pricing" && (
                    <AUMPricingSection 
                      section={section}
                      updateSection={updateSection}
                      headers={headers}
                      readOnly={isReadOnlyStatus(proposal.status)}
                    />
                  )}

                  {section.type === "image" && (
                    <div className="space-y-4">
                      <Input
                        value={section.content.url || ""}
                        onChange={(e) => updateSection(section.id, { url: e.target.value })}
                        placeholder="Image URL"
                        disabled={isReadOnlyStatus(proposal.status)}
                      />
                      <Input
                        value={section.content.caption || ""}
                        onChange={(e) => updateSection(section.id, { caption: e.target.value })}
                        placeholder="Caption (optional)"
                        disabled={isReadOnlyStatus(proposal.status)}
                      />
                      {section.content.url && (
                        <img 
                          src={section.content.url} 
                          alt={section.content.caption || "Preview"}
                          className="max-h-48 rounded-lg border border-stone"
                        />
                      )}
                    </div>
                  )}

                  {section.type === "signature" && (
                    <div className="space-y-4">
                      <Input
                        value={section.content.label || "Signature"}
                        onChange={(e) => updateSection(section.id, { label: e.target.value })}
                        placeholder="Label (e.g., Client Signature)"
                        disabled={isReadOnlyStatus(proposal.status)}
                      />
                      <div className="h-24 border-b-2 border-dashed border-stone flex items-end justify-center pb-2">
                        <span className="text-sm text-muted-foreground">Signature will appear here</span>
                      </div>
                    </div>
                  )}

                  {section.type === "advisor_info" && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-muted-foreground">Name</Label>
                          <Input
                            value={section.content.name || ""}
                            onChange={(e) => updateSection(section.id, { name: e.target.value })}
                            placeholder="Your name"
                            disabled={isReadOnlyStatus(proposal.status)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Title</Label>
                          <Input
                            value={section.content.title || ""}
                            onChange={(e) => updateSection(section.id, { title: e.target.value })}
                            placeholder="e.g., Financial Advisor"
                            disabled={isReadOnlyStatus(proposal.status)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Credentials / Designations</Label>
                        <Input
                          value={section.content.designations || ""}
                          onChange={(e) => updateSection(section.id, { designations: e.target.value })}
                          placeholder="e.g., CFP®, CFA"
                          disabled={isReadOnlyStatus(proposal.status)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Company</Label>
                        <Input
                          value={section.content.company_name || ""}
                          onChange={(e) => updateSection(section.id, { company_name: e.target.value })}
                          placeholder="Company name"
                          disabled={isReadOnlyStatus(proposal.status)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-muted-foreground">Phone</Label>
                          <Input
                            value={section.content.phone || ""}
                            onChange={(e) => updateSection(section.id, { phone: e.target.value })}
                            placeholder="(555) 123-4567"
                            disabled={isReadOnlyStatus(proposal.status)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Email</Label>
                          <Input
                            value={section.content.email || ""}
                            onChange={(e) => updateSection(section.id, { email: e.target.value })}
                            placeholder="you@company.com"
                            disabled={isReadOnlyStatus(proposal.status)}
                            autoComplete="off"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Website</Label>
                        <Input
                          value={section.content.company_website || ""}
                          onChange={(e) => updateSection(section.id, { company_website: e.target.value })}
                          placeholder="https://www.yourcompany.com"
                          disabled={isReadOnlyStatus(proposal.status)}
                        />
                      </div>
                      {/* Preview */}
                      <div className="bg-sand/30 rounded-lg p-4 mt-4">
                        <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                        <div className="flex items-start gap-3">
                          {section.content.profile_picture_url ? (
                            <img 
                              src={section.content.profile_picture_url} 
                              alt="Profile"
                              className="w-12 h-12 rounded-full object-cover border border-forest/20"
                            />
                          ) : (
                            <User className="w-10 h-10 text-forest p-2 bg-forest/10 rounded-full" />
                          )}
                          <div>
                            <p className="font-semibold text-forest">
                              {section.content.name || "Your Name"}
                              {section.content.designations && <span className="font-normal text-sm ml-1">{section.content.designations}</span>}
                            </p>
                            <p className="text-sm text-muted-foreground">{section.content.title || "Title"}</p>
                            <p className="text-sm">{section.content.company_name || "Company"}</p>
                            {(section.content.phone || section.content.email) && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {section.content.phone}{section.content.phone && section.content.email && " • "}{section.content.email}
                              </p>
                            )}
                            {section.content.company_website && (
                              <p className="text-sm text-forest">{section.content.company_website}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {section.type === "brokercheck" && (
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">BrokerCheck URL</Label>
                        <Input
                          value={section.content.link || ""}
                          onChange={(e) => updateSection(section.id, { link: e.target.value })}
                          placeholder="https://brokercheck.finra.org/..."
                          disabled={isReadOnlyStatus(proposal.status)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Description</Label>
                        <Input
                          value={section.content.description || ""}
                          onChange={(e) => updateSection(section.id, { description: e.target.value })}
                          placeholder="Verify my credentials on FINRA BrokerCheck"
                          disabled={isReadOnlyStatus(proposal.status)}
                        />
                      </div>
                      {/* Preview */}
                      <div className="bg-sand/30 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <Shield className="w-8 h-8 text-forest" />
                          <div>
                            <p className="font-medium text-forest">FINRA BrokerCheck</p>
                            <p className="text-sm text-muted-foreground">{section.content.description || "Verify credentials"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {section.type === "onboarding_software" && (
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Portal Name</Label>
                        <Input
                          value={section.content.name || ""}
                          onChange={(e) => updateSection(section.id, { name: e.target.value })}
                          placeholder="e.g., Client Portal"
                          disabled={isReadOnlyStatus(proposal.status)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Portal URL</Label>
                        <Input
                          value={section.content.link || ""}
                          onChange={(e) => updateSection(section.id, { link: e.target.value })}
                          placeholder="https://..."
                          disabled={isReadOnlyStatus(proposal.status)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Description</Label>
                        <Textarea
                          value={section.content.description || ""}
                          onChange={(e) => updateSection(section.id, { description: e.target.value })}
                          placeholder="Describe what clients can do in this portal..."
                          disabled={isReadOnlyStatus(proposal.status)}
                          className="min-h-[60px]"
                        />
                      </div>
                      {/* Preview */}
                      <div className="bg-sand/30 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <FolderOpen className="w-8 h-8 text-forest" />
                          <div>
                            <p className="font-medium text-forest">{section.content.name || "Client Portal"}</p>
                            <p className="text-sm text-muted-foreground">{section.content.description || "Access your client portal"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {proposal.sections.length === 0 && (
              <Card className="border-stone border-dashed">
                <CardContent className="py-16 text-center">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    {isReadOnlyStatus(proposal.status) 
                      ? "No sections in this proposal." 
                      : "No sections yet. Add sections using the sidebar or choose a template."}
                  </p>
                  {!isReadOnlyStatus(proposal.status) && (
                    <div className="flex justify-center gap-2">
                      <Button variant="outline" onClick={() => setShowTemplateDialog(true)}>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Use Template
                      </Button>
                      <Button variant="outline" onClick={() => addSection("text")}>
                        <Type className="w-4 h-4 mr-2" />
                        Add Text
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* DocuSign Tracker - Only show for accepted proposals */}
            {proposal.status === 'accepted' && (
              <DocuSignTracker
                proposalId={id}
                proposal={proposal}
                token={token}
                onUpdate={loadProposal}
              />
            )}

            {/* Payment Tracker - Only show for accepted proposals with flat fees */}
            {proposal.status === 'accepted' && (
              <PaymentTracker
                proposal={proposal}
                onUpdate={loadProposal}
              />
            )}

            {/* Prospect Info */}
            <Card className="border-stone">
              <CardHeader>
                <CardTitle className="font-display text-xl">Prospect</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Name *</Label>
                  <Input
                    value={proposal.prospect_name}
                    onChange={(e) => setProposal({...proposal, prospect_name: e.target.value})}
                    placeholder="John Smith"
                    className="mt-2"
                    data-testid="prospect-name-input"
                    disabled={isReadOnlyStatus(proposal.status)}
                  />
                </div>
                <div>
                  <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Email *</Label>
                  <Input
                    type="email"
                    value={proposal.prospect_email}
                    onChange={(e) => setProposal({...proposal, prospect_email: e.target.value})}
                    placeholder="john@example.com"
                    className="mt-2"
                    data-testid="prospect-email-input"
                    disabled={isReadOnlyStatus(proposal.status)}
                    autoComplete="off"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Valid Until */}
            <Card className="border-stone">
              <CardHeader>
                <CardTitle className="font-display text-xl">Valid Until</CardTitle>
              </CardHeader>
              <CardContent>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-left font-normal"
                      disabled={isReadOnlyStatus(proposal.status)}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {proposal.valid_until ? format(proposal.valid_until, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  {!isReadOnlyStatus(proposal.status) && (
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={proposal.valid_until}
                        onSelect={(date) => setProposal({...proposal, valid_until: date})}
                        initialFocus
                      />
                    </PopoverContent>
                  )}
                </Popover>
              </CardContent>
            </Card>

            {/* Total Amount */}
            <Card className="border-stone">
              <CardHeader>
                <CardTitle className="font-display text-xl">Total Amount</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const { pricingTotal, aumFeeTotal, total } = calculateTotal();
                  const hasBothTypes = pricingTotal > 0 && aumFeeTotal > 0;
                  
                  return (
                    <div className="space-y-3">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">$</span>
                        <Input
                          type="number"
                          value={proposal.total_amount || ""}
                          onChange={(e) => setProposal({...proposal, total_amount: parseFloat(e.target.value) || null})}
                          placeholder="0.00"
                          className="pl-8 font-mono text-xl"
                          data-testid="total-amount-input"
                          disabled={isReadOnlyStatus(proposal.status)}
                        />
                      </div>
                      
                      {hasBothTypes && (
                        <div className="text-sm space-y-1 pt-2 border-t border-stone/30">
                          <div className="flex justify-between text-muted-foreground">
                            <span>Pricing items</span>
                            <span className="font-mono">${pricingTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between text-muted-foreground">
                            <span>AUM advisory fee</span>
                            <span className="font-mono">${aumFeeTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground">
                        Auto-calculated from pricing & AUM sections
                        {pricingTotal > 0 && aumFeeTotal > 0 && (
                          <span className="block mt-1 text-amber-600">
                            Note: Only pricing items will be invoiced through Stripe
                          </span>
                        )}
                      </p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* DocuSign Agreement - Only show for draft/sent proposals */}
            {!isReadOnlyStatus(proposal.status) && (
              <DocuSignAgreementCard
                proposal={proposal}
                token={token}
                user={user}
                onChange={(config) => setProposal(prev => ({ ...prev, docusign_config: config }))}
                docusignConnected={docusignConnected}
              />
            )}

            {/* Payment Automation - Only show for draft/sent with pricing */}
            {!isReadOnlyStatus(proposal.status) && proposal.sections?.some(s => s.type === "pricing") && (
              <Card className={`border-stone ${stripeConnected === false ? 'border-amber-300' : ''}`}>
                <CardHeader>
                  <CardTitle className="font-display text-xl flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Payment Automation
                    {stripeConnected === false && (
                      <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Not connected</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stripeConnected === false ? (
                    <div className="text-center py-4">
                      <DollarSign className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-3">
                        Connect Stripe to automatically send payment requests when prospects accept.
                      </p>
                      <Button asChild variant="outline" size="sm" className="border-forest text-forest hover:bg-forest/5">
                        <Link to="/integrations">
                          Connect Stripe
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Warning if auto-send is enabled but stripe disconnected */}
                      {proposal.payment_config?.auto_send_on_accept && stripeConnected === false && (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm mb-4">
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-amber-800 font-medium">Stripe disconnected</p>
                            <p className="text-amber-700 text-xs mt-1">
                              Payment auto-send is enabled but Stripe isn't connected.{" "}
                              <Link to="/integrations" className="text-forest font-medium hover:underline">
                                Reconnect Stripe
                              </Link>
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-forest">Auto-send payment request</p>
                          <p className="text-xs text-muted-foreground">Send payment link when prospect accepts</p>
                        </div>
                        <Switch
                          checked={proposal.payment_config?.auto_send_on_accept || false}
                          onCheckedChange={(v) => setProposal(prev => ({
                            ...prev,
                            payment_config: { ...prev.payment_config, auto_send_on_accept: v }
                          }))}
                          data-testid="payment-auto-send-toggle"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-stone/50">
                        Manage connection in{" "}
                        <Link to="/integrations" className="text-forest hover:underline">
                          Settings → Integrations
                        </Link>
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Follow-up Reminders Override - Only show for draft/sent proposals */}
            {!isReadOnlyStatus(proposal.status) && (
              <Card className={`border-stone ${!globalReminderSettings?.reminders_enabled ? 'border-amber-300' : ''}`}>
                <CardHeader>
                  <CardTitle className="font-display text-xl flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Follow-up Reminders
                    {!globalReminderSettings?.reminders_enabled && (
                      <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Not configured</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!globalReminderSettings?.reminders_enabled ? (
                    <div className="text-center py-4">
                      <Bell className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-3">
                        Follow-up reminders aren't set up yet. Configure them to automatically remind prospects or yourself about pending proposals.
                      </p>
                      <Button asChild variant="outline" size="sm" className="border-forest text-forest hover:bg-forest/5">
                        <Link to="/settings?tab=reminders">
                          Set Up Reminders
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Warning if email sender is personal email but not connected */}
                      {globalReminderSettings?.reminder_mode === 'auto' && 
                       globalReminderSettings?.email_sender === 'gmail' && 
                       !emailConnected && 
                       proposal.reminder_settings_override?.enabled !== false && (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm mb-4">
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-amber-800 font-medium">Email disconnected</p>
                            <p className="text-amber-700 text-xs mt-1">
                              Your email is set as the sender but isn't connected. Reminders won't be sent until you{" "}
                              <Link to="/integrations" className="text-forest font-medium hover:underline">
                                reconnect your email
                              </Link>.
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-forest">Use global reminder settings</p>
                          <p className="text-xs text-muted-foreground">
                            {proposal.reminder_settings_override?.enabled === false 
                              ? "Auto-reminders to prospect disabled" 
                              : "Uses your global reminder settings"}
                          </p>
                        </div>
                        <Switch
                          checked={proposal.reminder_settings_override?.enabled !== false}
                          onCheckedChange={(v) => setProposal(prev => ({
                            ...prev,
                            reminder_settings_override: v ? null : { enabled: false, notify_advisor: true }
                          }))}
                          data-testid="reminder-override-toggle"
                        />
                      </div>
                      
                      {/* Show advisor notification toggle when main toggle is OFF */}
                      {proposal.reminder_settings_override?.enabled === false && (
                        <div className="flex items-center justify-between p-3 bg-sand/50 rounded-lg border border-stone/50">
                          <div>
                            <p className="text-sm font-medium text-forest">Still notify me to follow up</p>
                            <p className="text-xs text-muted-foreground">
                              Get reminders so you can follow up manually
                            </p>
                          </div>
                          <Switch
                            checked={proposal.reminder_settings_override?.notify_advisor !== false}
                            onCheckedChange={(v) => setProposal(prev => ({
                              ...prev,
                              reminder_settings_override: { 
                                ...prev.reminder_settings_override, 
                                notify_advisor: v 
                              }
                            }))}
                            data-testid="reminder-notify-advisor-toggle"
                          />
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground pt-2 border-t border-stone/50">
                        Configure global settings in{" "}
                        <Link to="/settings?tab=reminders" className="text-forest hover:underline">
                          Settings → Follow-up Reminders
                        </Link>
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Welcome Email Automation - Show for draft/sent proposals */}
            {!isReadOnlyStatus(proposal.status) && (
              <Card className={`border-stone ${!emailConnected ? 'border-amber-300' : ''}`}>
                <CardHeader>
                  <CardTitle className="font-display text-xl flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Welcome Email
                    {emailConnected ? (
                      <span className="text-xs font-normal text-forest/60 bg-forest/10 px-2 py-0.5 rounded">Auto</span>
                    ) : (
                      <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Email not connected</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  
                  {/* Email Not Connected Warning */}
                  {!emailConnected ? (
                    <div className="text-center py-4">
                      <Mail className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-3">
                        Connect your email to automatically send welcome emails from your address after prospects accept.
                      </p>
                      <Button asChild variant="outline" size="sm" className="border-forest text-forest hover:bg-forest/5">
                        <Link to="/integrations">
                          Connect Email
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        A welcome email with next steps is automatically sent 3 hours after acceptance from your connected email.
                      </p>
                      
                      {/* Send Welcome Email Toggle - FIRST */}
                      <div className="flex items-center justify-between p-3 bg-sand/50 rounded-lg border border-stone/30">
                        <div>
                          <p className="text-sm font-medium text-forest">Send welcome email</p>
                          <p className="text-xs text-muted-foreground">
                            {proposal.followup_settings?.disabled 
                              ? "Welcome email disabled for this proposal" 
                              : "Includes progress update and next steps"}
                          </p>
                        </div>
                        <Switch
                          checked={!proposal.followup_settings?.disabled}
                          onCheckedChange={(v) => setProposal(prev => ({
                            ...prev,
                            followup_settings: { 
                              ...prev.followup_settings, 
                              disabled: !v 
                            }
                          }))}
                          data-testid="welcome-email-toggle"
                        />
                      </div>
                      
                      {/* Warning when welcome email is disabled */}
                      {proposal.followup_settings?.disabled && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-xs text-amber-800">
                            <strong>Note:</strong> With welcome email disabled, prospects won't receive automated progress updates, next steps links, or any scheduled follow-up communication after acceptance.
                          </p>
                        </div>
                      )}
                      
                      {/* Only show these options when welcome email is enabled */}
                      {!proposal.followup_settings?.disabled && (
                        <>
                          {/* Preview Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => setShowWelcomeEmailPreview(true)}
                            data-testid="preview-welcome-email-btn"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Preview & Customize Email
                          </Button>
                          
                          {/* Include First Meeting Link */}
                          <div className="flex items-center justify-between p-3 bg-sand/30 rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-forest">Include First Meeting Link</p>
                              <p className="text-xs text-muted-foreground">
                                {user?.first_meeting_link 
                                  ? "Link to schedule first client meeting" 
                                  : <span className="text-amber-600">Not configured in Settings</span>}
                              </p>
                            </div>
                            <Switch
                              checked={proposal.followup_settings?.include_first_meeting !== false}
                              onCheckedChange={(v) => setProposal(prev => ({
                                ...prev,
                                followup_settings: { 
                                  ...prev.followup_settings, 
                                  include_first_meeting: v 
                                }
                              }))}
                              disabled={!user?.first_meeting_link}
                              data-testid="include-first-meeting-toggle"
                            />
                          </div>
                          
                          {/* Include Onboarding Link */}
                          <div className="flex items-center justify-between p-3 bg-sand/30 rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-forest">Include Onboarding Link</p>
                              <p className="text-xs text-muted-foreground">
                                {user?.onboarding_link 
                                  ? "Link to self-service onboarding" 
                                  : <span className="text-amber-600">Not configured in Settings</span>}
                              </p>
                            </div>
                            <Switch
                              checked={proposal.followup_settings?.include_onboarding !== false}
                              onCheckedChange={(v) => setProposal(prev => ({
                                ...prev,
                                followup_settings: { 
                                  ...prev.followup_settings, 
                                  include_onboarding: v 
                                }
                              }))}
                              disabled={!user?.onboarding_link}
                              data-testid="include-onboarding-toggle"
                            />
                          </div>
                          
                          {/* Include File Share Link */}
                          <div className="flex items-center justify-between p-3 bg-sand/30 rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-forest">Include File Share Link</p>
                              <p className="text-xs text-muted-foreground">
                                {user?.secure_file_share_link 
                                  ? "Link to secure document sharing" 
                                  : <span className="text-amber-600">Not configured in Settings</span>}
                              </p>
                            </div>
                            <Switch
                              checked={proposal.followup_settings?.include_file_share === true}
                              onCheckedChange={(v) => setProposal(prev => ({
                                ...prev,
                                followup_settings: { 
                                  ...prev.followup_settings, 
                                  include_file_share: v 
                                }
                              }))}
                              disabled={!user?.secure_file_share_link}
                              data-testid="include-file-share-toggle"
                            />
                          </div>
                        </>
                      )}
                      
                      <p className="text-xs text-muted-foreground pt-2 border-t border-stone/50">
                        Configure your links in{" "}
                        <Link to="/settings?tab=general" className="text-forest hover:underline">
                          Settings → General
                        </Link>
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Welcome Email Preview Modal */}
            <Dialog open={showWelcomeEmailPreview} onOpenChange={setShowWelcomeEmailPreview}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">Customize Welcome Email</DialogTitle>
                  <DialogDescription>
                    Edit the email sent to {proposal.prospect_name || "prospect"} after they accept your proposal
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Email Subject */}
                  <div>
                    <Label className="text-sm font-medium">Email Subject</Label>
                    <Input
                      value={proposal.followup_settings?.subject || `Welcome to ${user?.company_name || "Our Company"} - Your Next Steps`}
                      onChange={(e) => setProposal(prev => ({
                        ...prev,
                        followup_settings: {
                          ...prev.followup_settings,
                          subject: e.target.value
                        }
                      }))}
                      placeholder="Email subject line..."
                      className="mt-1"
                    />
                  </div>

                  {/* Email Header */}
                  <div>
                    <Label className="text-sm font-medium">Email Header</Label>
                    <Input
                      value={proposal.followup_settings?.header || `Welcome to ${user?.company_name || "Our Company"}!`}
                      onChange={(e) => setProposal(prev => ({
                        ...prev,
                        followup_settings: {
                          ...prev.followup_settings,
                          header: e.target.value
                        }
                      }))}
                      placeholder="Email header text..."
                      className="mt-1"
                    />
                  </div>

                  {/* Greeting */}
                  <div>
                    <Label className="text-sm font-medium">Greeting</Label>
                    <Input
                      value={proposal.followup_settings?.greeting || `Hi ${proposal.prospect_name || "[Name]"},`}
                      onChange={(e) => setProposal(prev => ({
                        ...prev,
                        followup_settings: {
                          ...prev.followup_settings,
                          greeting: e.target.value
                        }
                      }))}
                      placeholder="Hi [Name],"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Use [Name] to auto-insert prospect's name</p>
                  </div>

                  {/* Main Body */}
                  <div>
                    <Label className="text-sm font-medium">Main Message</Label>
                    <Textarea
                      value={proposal.followup_settings?.main_body || "Thank you for choosing to work with me! I'm excited to get started."}
                      onChange={(e) => setProposal(prev => ({
                        ...prev,
                        followup_settings: {
                          ...prev.followup_settings,
                          main_body: e.target.value
                        }
                      }))}
                      placeholder="Your main message..."
                      className="mt-1 min-h-[100px]"
                    />
                  </div>

                  {/* Progress Section Toggle */}
                  <div className="flex items-center justify-between p-3 bg-sand/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">Include Progress Update</p>
                      <p className="text-xs text-muted-foreground">Shows status of DocuSign & Payment</p>
                    </div>
                    <Switch
                      checked={proposal.followup_settings?.include_progress !== false}
                      onCheckedChange={(v) => setProposal(prev => ({
                        ...prev,
                        followup_settings: {
                          ...prev.followup_settings,
                          include_progress: v
                        }
                      }))}
                    />
                  </div>

                  {/* Next Steps Toggles */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Include Next Step Links</Label>
                    
                    <div className="flex items-center justify-between p-2 border rounded">
                      <span className="text-sm">Schedule First Meeting</span>
                      <Switch
                        checked={proposal.followup_settings?.include_first_meeting !== false}
                        onCheckedChange={(v) => setProposal(prev => ({
                          ...prev,
                          followup_settings: { ...prev.followup_settings, include_first_meeting: v }
                        }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-2 border rounded">
                      <span className="text-sm">Start Onboarding</span>
                      <Switch
                        checked={proposal.followup_settings?.include_onboarding !== false}
                        onCheckedChange={(v) => setProposal(prev => ({
                          ...prev,
                          followup_settings: { ...prev.followup_settings, include_onboarding: v }
                        }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-2 border rounded">
                      <span className="text-sm">Secure File Share</span>
                      <Switch
                        checked={proposal.followup_settings?.include_file_share === true}
                        onCheckedChange={(v) => setProposal(prev => ({
                          ...prev,
                          followup_settings: { ...prev.followup_settings, include_file_share: v }
                        }))}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Links are pulled from your <Link to="/settings?tab=general" className="text-forest hover:underline">Settings</Link>
                    </p>
                  </div>

                  {/* Closing Message */}
                  <div>
                    <Label className="text-sm font-medium">Closing Message</Label>
                    <Textarea
                      value={proposal.followup_settings?.closing || "Questions? Simply reply to this email and I'll be happy to help!"}
                      onChange={(e) => setProposal(prev => ({
                        ...prev,
                        followup_settings: {
                          ...prev.followup_settings,
                          closing: e.target.value
                        }
                      }))}
                      placeholder="Your closing message..."
                      className="mt-1 min-h-[60px]"
                    />
                  </div>

                  {/* Email Signature */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium">Email Signature</Label>
                      <Switch
                        checked={proposal.followup_settings?.include_signature !== false}
                        onCheckedChange={(v) => setProposal(prev => ({
                          ...prev,
                          followup_settings: { ...prev.followup_settings, include_signature: v }
                        }))}
                      />
                    </div>
                    
                    {proposal.followup_settings?.include_signature !== false && (
                      <div className="space-y-3 p-3 bg-sand/30 rounded-lg">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">Name</Label>
                            <Input
                              value={proposal.followup_settings?.signature_name || user?.name || ""}
                              onChange={(e) => setProposal(prev => ({
                                ...prev,
                                followup_settings: { ...prev.followup_settings, signature_name: e.target.value }
                              }))}
                              placeholder="Your name"
                              className="mt-1 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Title</Label>
                            <Input
                              value={proposal.followup_settings?.signature_title || user?.title || ""}
                              onChange={(e) => setProposal(prev => ({
                                ...prev,
                                followup_settings: { ...prev.followup_settings, signature_title: e.target.value }
                              }))}
                              placeholder="Your title"
                              className="mt-1 text-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">Company</Label>
                            <Input
                              value={proposal.followup_settings?.signature_company || user?.company_name || ""}
                              onChange={(e) => setProposal(prev => ({
                                ...prev,
                                followup_settings: { ...prev.followup_settings, signature_company: e.target.value }
                              }))}
                              placeholder="Company name"
                              className="mt-1 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Phone</Label>
                            <Input
                              value={proposal.followup_settings?.signature_phone || user?.phone || ""}
                              onChange={(e) => setProposal(prev => ({
                                ...prev,
                                followup_settings: { ...prev.followup_settings, signature_phone: e.target.value }
                              }))}
                              placeholder="Phone number"
                              className="mt-1 text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Website</Label>
                          <Input
                            value={proposal.followup_settings?.signature_website || user?.company_website || ""}
                            onChange={(e) => setProposal(prev => ({
                              ...prev,
                              followup_settings: { ...prev.followup_settings, signature_website: e.target.value }
                            }))}
                            placeholder="https://yourcompany.com"
                            className="mt-1 text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Preview */}
                  <div className="border-t pt-4">
                    <Label className="text-sm font-medium mb-2 block">Preview</Label>
                    <div className="border rounded-lg overflow-hidden text-sm">
                      <div className="bg-forest text-white p-3 text-center">
                        <strong>{proposal.followup_settings?.header || `Welcome to ${user?.company_name || "Our Company"}!`}</strong>
                      </div>
                      <div className="p-4 bg-white space-y-3">
                        <p>{(proposal.followup_settings?.greeting || `Hi ${proposal.prospect_name || "[Name]"},`).replace('[Name]', proposal.prospect_name || 'there')}</p>
                        <p>{proposal.followup_settings?.main_body || "Thank you for choosing to work with me! I'm excited to get started."}</p>
                        
                        {proposal.followup_settings?.include_progress !== false && (
                          <div className="bg-gray-50 rounded p-3 text-xs">
                            <p className="font-semibold mb-2">Progress Update</p>
                            <p className="text-gray-500 italic">DocuSign & Payment status will appear here</p>
                          </div>
                        )}
                        
                        {(proposal.followup_settings?.include_first_meeting !== false || 
                          proposal.followup_settings?.include_onboarding !== false ||
                          proposal.followup_settings?.include_file_share) && (
                          <div className="bg-sand/50 rounded p-3 text-xs">
                            <p className="font-semibold mb-2">Your Next Steps</p>
                            <p className="text-gray-500 italic">Action buttons will appear here based on your settings</p>
                          </div>
                        )}
                        
                        <hr />
                        <p className="text-gray-500 text-xs">{proposal.followup_settings?.closing || "Questions? Simply reply to this email and I'll be happy to help!"}</p>
                        
                        {/* Signature Preview */}
                        {proposal.followup_settings?.include_signature !== false && (
                          <div className="border-t pt-3 mt-3">
                            <p className="font-medium text-forest">
                              {proposal.followup_settings?.signature_name || user?.name || "Your Name"}
                            </p>
                            {(proposal.followup_settings?.signature_title || user?.title) && (
                              <p className="text-gray-600 text-xs">{proposal.followup_settings?.signature_title || user?.title}</p>
                            )}
                            {(proposal.followup_settings?.signature_company || user?.company_name) && (
                              <p className="text-gray-600 text-xs">{proposal.followup_settings?.signature_company || user?.company_name}</p>
                            )}
                            {(proposal.followup_settings?.signature_phone || user?.phone) && (
                              <p className="text-gray-500 text-xs">{proposal.followup_settings?.signature_phone || user?.phone}</p>
                            )}
                            {(proposal.followup_settings?.signature_website || user?.company_website) && (
                              <p className="text-forest text-xs">{proposal.followup_settings?.signature_website || user?.company_website}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-3 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-muted-foreground">Save options:</p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setShowWelcomeEmailPreview(false)}>
                        Cancel
                      </Button>
                      <Button 
                        variant="outline"
                        className="border-forest text-forest hover:bg-forest/10"
                        onClick={async () => {
                          // Save as global default
                          try {
                            await axios.put(`${API}/settings`, {
                              welcome_email_defaults: {
                                subject: proposal.followup_settings?.subject,
                                header: proposal.followup_settings?.header,
                                greeting: proposal.followup_settings?.greeting,
                                main_body: proposal.followup_settings?.main_body,
                                closing: proposal.followup_settings?.closing,
                                include_progress: proposal.followup_settings?.include_progress,
                                include_first_meeting: proposal.followup_settings?.include_first_meeting,
                                include_onboarding: proposal.followup_settings?.include_onboarding,
                                include_file_share: proposal.followup_settings?.include_file_share,
                                include_signature: proposal.followup_settings?.include_signature,
                                signature_name: proposal.followup_settings?.signature_name,
                                signature_title: proposal.followup_settings?.signature_title,
                                signature_company: proposal.followup_settings?.signature_company,
                                signature_phone: proposal.followup_settings?.signature_phone,
                                signature_website: proposal.followup_settings?.signature_website
                              }
                            }, { headers, withCredentials: true });
                            setShowWelcomeEmailPreview(false);
                            toast.success("Saved as default for all future proposals");
                          } catch (error) {
                            toast.error("Failed to save defaults");
                          }
                        }}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save as Default
                      </Button>
                      <Button 
                        className="bg-forest hover:bg-forest/90"
                        onClick={() => {
                          setShowWelcomeEmailPreview(false);
                          toast.success("Saved for this proposal");
                        }}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save for This Proposal
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground text-right">
                      "Save as Default" applies to all new proposals. "Save for This Proposal" only affects this one.
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Attachments - Always visible, auto-saves on first upload if new proposal */}
            <Card className="border-stone">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Paperclip className="w-5 h-5" />
                  Attachments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  className="hidden"
                  data-testid="file-input"
                  disabled={isReadOnlyStatus(proposal.status)}
                />
                
                {/* Upload button - hide for read-only */}
                {!isReadOnlyStatus(proposal.status) && (
                  <Button
                    variant="outline"
                    className="w-full mb-4"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-attachment-btn"
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {uploading ? "Uploading..." : "Upload Document"}
                  </Button>
                )}
                
                {!isReadOnlyStatus(proposal.status) && (
                  <p className="text-xs text-muted-foreground mb-4">
                    PDF, Word, Excel • Max 10MB
                  </p>
                )}
                
                {/* Attachments list */}
                {attachments.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No attachments
                  </p>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.attachment_id}
                        className="flex items-center gap-3 p-3 bg-sand rounded-lg group"
                      >
                        {getFileIcon(attachment.file_type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-forest truncate">
                            {attachment.filename}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(attachment.size)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => downloadAttachment(attachment.attachment_id, attachment.filename)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {!isReadOnlyStatus(proposal.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => deleteAttachment(attachment.attachment_id, attachment.filename)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProposalBuilder;
